// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  GetSparseAssetsOptions,
  getSparseAssets,
} from '@/asset/api/get-sparse-assets';
import { AssetAssignmentStatus } from '@/constants/asset-assignment-status';
import { REFERENCE_DATA_QUERY_OPTIONS } from '@/utilities/query-profiles';
import { useQuery } from '@tanstack/react-query';

/** Maximum number of results to display in sparse asset search */
export const SPARSE_ASSET_MAX_RESULTS = 200;

interface UseSparseAssetSearchOptions {
  /** Search term to filter by serial number, product name, or SKU */
  query?: string;
  /** Filter by scrapped status (default: false for non-scrapped) */
  scrapped?: boolean;
  /** Filter by project assignment status (only 'none' is currently supported) */
  withProjectStatus?: AssetAssignmentStatus[];
  /** Whether the query should be enabled */
  enabled?: boolean;
}

/**
 * React Query hook for searching sparse assets.
 * Fetches a paginated list of sparse assets based on the provided search criteria.
 * Results are limited to SPARSE_ASSET_MAX_RESULTS (300) items.
 */
export function useSparseAssetSearch(
  options: UseSparseAssetSearchOptions = {}
) {
  const queryOptions: GetSparseAssetsOptions = {
    query: options.query || undefined,
    limit: SPARSE_ASSET_MAX_RESULTS,
    scrapped: options.scrapped,
    withProjectStatus: options.withProjectStatus,
  };

  return useQuery({
    queryKey: ['sparse-assets', queryOptions],
    queryFn: () => getSparseAssets(queryOptions),
    enabled: options.enabled !== false,
    ...REFERENCE_DATA_QUERY_OPTIONS,
  });
}
